// Modal de atualização de data de vencimento (Story 4.8).
// Trigger: linha de cobrança com status `enviada` ou `vencida` na listagem.

import * as Dialog from '@radix-ui/react-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  addDaysLocal,
  todayLocal,
  updateDueDateSchema,
  type UpdateDueDateRequest,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input } from '../clients/form-primitives';
import { PillButton } from '../clients/PillButton';
import { useUpdateChargeDueDate } from '../../hooks/use-charges';
import { extractErrorMessage } from '../../lib/errors';
import { firstErrorMessage } from '../../lib/form-errors';

interface UpdateDueDateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  charge: { id: string; clientName: string; description: string; dueDate: string } | null;
}

export function UpdateDueDateModal({
  open,
  onOpenChange,
  charge,
}: UpdateDueDateModalProps): JSX.Element {
  const mutation = useUpdateChargeDueDate();

  // Default: hoje + 7 dias (mesma janela do cron de cobrança)
  const tomorrow = addDaysLocal(todayLocal(), 1);
  const defaultDate = addDaysLocal(todayLocal(), 7);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateDueDateRequest>({
    resolver: zodResolver(updateDueDateSchema),
    defaultValues: {
      newDueDate: defaultDate,
      sendNotification: true,
    },
  });

  async function onSubmit(values: UpdateDueDateRequest): Promise<void> {
    if (!charge) return;
    try {
      const result = await mutation.mutateAsync({ chargeId: charge.id, payload: values });
      toast.success(`Vencimento atualizado pra ${result.newDueDate}`, {
        description: result.notificationEnqueued
          ? 'Cliente vai receber novo link com juros/multa preservados'
          : 'Sem notificação ao cliente',
      });
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao atualizar vencimento'));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(480px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[12px] border border-line bg-panel p-6 shadow-2xl',
          )}
        >
          <div className="mb-5 flex items-center justify-between">
            <div>
              <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em]">
                Adiar vencimento
              </Dialog.Title>
              {charge && (
                <Dialog.Description className="mt-1 text-[12px] text-muted">
                  {charge.clientName} · {charge.description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="rounded-[8px] p-1.5 text-muted hover:bg-panel-2 hover:text-ink"
              >
                <X size={16} />
              </button>
            </Dialog.Close>
          </div>

          {/* Aviso sobre preservação de juros/multa */}
          <div className="mb-5 flex items-start gap-2 rounded-[8px] border border-line bg-panel-2 p-3">
            <Info size={14} strokeWidth={1.8} className="mt-0.5 shrink-0 text-muted" />
            <p className="text-[12px] leading-[1.5] text-ink-2">
              O Asaas mantém o <strong>mesmo link de pagamento</strong> e preserva os{' '}
              <strong>juros/multa</strong> já acumulados. Se a cobrança estava <em>vencida</em>,
              volta para <em>enviada</em>.
            </p>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit, (errs) =>
              toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
            )}
            className="flex flex-col gap-4"
          >
            <Field
              label="Nova data de vencimento"
              required
              error={errors.newDueDate?.message}
              hint="Mínimo: amanhã"
            >
              {({ inputId }) => (
                <Input
                  id={inputId}
                  type="date"
                  min={tomorrow}
                  hasError={Boolean(errors.newDueDate)}
                  {...register('newDueDate')}
                />
              )}
            </Field>

            <label className="flex items-start gap-2 text-[13px] text-ink-2">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-ink"
                {...register('sendNotification')}
              />
              <span>
                <span className="font-medium">Reenviar notificação ao cliente</span>
                <span className="mt-0.5 block text-[11px] text-muted">
                  Cliente recebe o novo link via WhatsApp/email (story 5.6)
                </span>
              </span>
            </label>

            <div className="mt-2 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={() => onOpenChange(false)}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={mutation.isPending}>
                {mutation.isPending ? 'Atualizando…' : 'Confirmar nova data'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
